import React, { PropsWithChildren } from 'react';
import { Platform, TextProps } from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../constants/Colors';
import { B } from '../Typography';

const StyledView = styled.View`
  flex: 1;
`;

const StyledPrimary = styled(B)<{ accessoryPadding: number; color?: string }>`
  padding-right: ${p => p.accessoryPadding}px;
  color: ${p => p.color || Colors.darkText};
` as React.FC<
  TextProps & ListItemTextProps & { accessoryPadding: number; color?: string }
>;

const StyledSecondaryText = styled.Text<{
  small?: boolean;
  large?: boolean;
  accessoryPadding: number;
  color?: string;
}>`
  color: ${p => p.color || Colors.darkText};
  font-family: ${p =>
    p.large
      ? 'WorkSans-SemiBold'
      : 'WorkSans-Regular'}; /* Font Weight 700 or 300 */
  font-size: 14px;
  line-height: 16px;
  padding-right: ${p => p.accessoryPadding}px;
  margin-top: 4px;
  /* -webkit-line-clamp: 2; */
  overflow: hidden;
  /* text-overflow: ellipsis; */
  text-decoration: underline;
`;

export interface ListItemTextProps {
  primary?: string | JSX.Element;
  secondary?: string | JSX.Element;
  smallSecondary?: string | JSX.Element;
  largeSecondary?: string | JSX.Element;
  accessoryPadding?: boolean | number;
  extendedDivider?: boolean;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  primaryTextNumberOfLines?: number;
}

/**
 * Used inside a `ListItem` component to display primary and secondary text in
 * a list. The component only displays text on one line with ellipsis if too
 * long.
 * @param primary string or JSX.Element of primary content
 * @param secondary string or JSX.Element of secondary content
 * @param smallSecondary use smaller font for secondary text
 * @param largeSecondary use larger font for secondary text
 * @param accessoryPadding set right padding for accessory
 * @param extendedDivider if the extended divider is used
 * @param primaryTextColor set color of primary text
 * @param secondaryTextColor set color of secondary text
 * @param primaryTextNumberOfLines set number of lines for primary text
 */
const ListItemText: React.FC<PropsWithChildren & ListItemTextProps> = ({
  primary,
  secondary,
  children,
  smallSecondary,
  largeSecondary,
  accessoryPadding,
  extendedDivider,
  primaryTextColor,
  secondaryTextColor,
  primaryTextNumberOfLines
}) => {
  if (typeof accessoryPadding === 'boolean') {
    accessoryPadding = 12;
  } else if (typeof accessoryPadding === 'undefined') {
    accessoryPadding = 12;
  }
  if (extendedDivider) {
    accessoryPadding += 12;
  }
  // web override for line cap
  const style =
    Platform.OS === 'web'
      ? {
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }
      : {};
  return (
    <StyledView>
      {primary && (
        <StyledPrimary
          color={primaryTextColor}
          accessoryPadding={accessoryPadding}
          numberOfLines={primaryTextNumberOfLines || 1}
        >
          {primary}
        </StyledPrimary>
      )}
      {secondary && (
        <StyledSecondaryText
          color={secondaryTextColor}
          accessoryPadding={accessoryPadding}
          numberOfLines={3}
          style={style as any}
        >
          {secondary}
        </StyledSecondaryText>
      )}
      {smallSecondary && (
        <StyledSecondaryText
          color={secondaryTextColor}
          small
          accessoryPadding={accessoryPadding}
          numberOfLines={2}
        >
          {smallSecondary}
        </StyledSecondaryText>
      )}
      {largeSecondary && (
        <StyledSecondaryText
          color={secondaryTextColor}
          large
          accessoryPadding={accessoryPadding}
          numberOfLines={2}
          style={style as any}
        >
          {largeSecondary}
        </StyledSecondaryText>
      )}
      {children && (
        <StyledPrimary
          color={primaryTextColor}
          accessoryPadding={accessoryPadding}
        >
          {children}
        </StyledPrimary>
      )}
    </StyledView>
  );
};

export default ListItemText;
